import { z } from "zod";
import { VariableGroupEnum } from "../../../enum/nodes.enum";
import { useVariableInputStore } from "../../../store/variableInput.store";
import { stringContainsValidVariable } from "../../../utils/node.util";

export const httpActionNodeSchema = z.object({
  nodeId: z.string(),
  actionName: z
    .string()
    .min(1, "Please enter valid name")
    .max(45, "Name should be precise and less than 45 characters")
    .regex(
      /^[a-zA-Z0-9_ -]*$/,
      "Name can only contain alphanumeric characters, `-` and `_`",
    ),
  actionPurpose: z.string().optional(),
  method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"], {
    errorMap: () => ({
      message: "Please select a valid method",
    }),
  }),
  url: z
    .string()
    .url("Please enter valid URL")
    .refine((value) => {
      const validVariables = useVariableInputStore.getState().variables;
      return stringContainsValidVariable(value, validVariables);
    }, "Incorrect Variable used")
    .refine((value) => {
      if (!value) return false;

      try {
        // if running in local, we allow it for testing
        const windowLocation = window?.location?.pathname;

        const isLocal = ["localhost", "127.0.0.1"].some((v) =>
          windowLocation.includes(v),
        );
        if (isLocal) return true;

        const url = new URL(value);
        // reject localhost
        if (url.hostname === "localhost") return false;

        // reject url with port
        if (url.host.includes(":")) return false;

        // reject any IPv4 address like 127.0.0.1, 192.168.0.1, 10.0.0.1, etc.
        const ipv4Pattern =
          /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
        if (ipv4Pattern.test(url.hostname)) return false;

        return true;
      } catch {
        return false;
      }
    }, "URL is invalid"),
  headers: z.array(
    z.object({
      key: z.string(),
      value: z.string().refine((value) => {
        const validVariables = useVariableInputStore.getState().variables;
        const systemVariables = {
          key: "hubspot_token",
          value: "hubspot_token",
          prefix: "",
          group: VariableGroupEnum.SYSTEM_ATTRIBUTES,
        };
        return stringContainsValidVariable(value, [
          ...validVariables,
          systemVariables,
        ]);
      }, "Incorrect Variable used"),
    }),
  ),
  params: z.array(
    z.object({
      key: z.string(),
      value: z.string().refine((value) => {
        const validVariables = useVariableInputStore.getState().variables;
        return stringContainsValidVariable(value, validVariables);
      }, "Incorrect Variable used"),
    }),
  ),
  responseFields: z.array(
    z.object({
      path: z.string(),
      attributeId: z.string(),
      responseValue: z.unknown(),
    }),
  ),
  body: z
    .string()
    .refine((value) => {
      const validVariables = useVariableInputStore.getState().variables;
      return stringContainsValidVariable(value, validVariables);
    }, "Incorrect Variable used")
    .refine((value) => {
      if (!value) return true;
      const jsonString = `${value}`;

      try {
        JSON.parse(jsonString);
        return true;
      } catch (error) {
        return false;
      }
    }, "Invalid JSON body")
    .optional(),
  paths: z
    .array(
      z.object({
        type: z.enum(["success", "failure"]),
      }),
    )
    .default([{ type: "success" }, { type: "failure" }]),
  isValid: z.boolean().default(true),
});

export type HttpActionNodeSchema = z.infer<typeof httpActionNodeSchema>;
